/**
 * VoiceButton — Phase 5 Voice Assistant
 *
 * Records audio via MediaRecorder, sends to POST /api/ai/voice-intent,
 * and calls onResult(intentData) when classification succeeds.
 *
 * CRITICAL: Never auto-executes any action from the intent result.
 * Always calls onResult() — the parent must show VoiceConfirmModal before
 * any write action executes.
 *
 * Shows a "Use Chrome" banner on unsupported browsers instead of the mic button.
 * Phase 6 (Expo) replaces this component with expo-av for mobile voice capture.
 *
 * Props:
 *   language   {string}   — 'english' | 'tamil' | 'mixed' (default: 'english')
 *   onResult   {Function} — called with intent data { intent, entity, confidence, suggestedAction, transcript }
 *   onError    {Function} — called with error message string
 *   disabled   {boolean}  — disables the button (e.g. while modal is open)
 *
 * Role is NOT a prop — it is derived server-side from the authenticated user's JWT.
 * Never pass role from the frontend to prevent patients from requesting admin-scoped intents.
 */

import React, { useState, useRef, useCallback } from 'react';
import api from '../../utils/api';
import { supportsVoice, getSupportedMimeType, getBrowserWarning } from '../../utils/browserDetect';

const MAX_RECORDING_MS = 30000;  // 30 s — prevents runaway recordings and Whisper oversized uploads

export default function VoiceButton({ language = 'english', onResult, onError, disabled = false }) {
  const [recording, setRecording]   = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const autoStopRef      = useRef(null);

  const browserWarning = getBrowserWarning();

  // ── Stop recording ─────────────────────────────────────────────────────────
  // Declared before startRecording so startRecording can reference it without
  // a circular dependency. Only uses refs → empty dependency array is correct.
  const stopRecording = useCallback(() => {
    clearTimeout(autoStopRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // ── Start recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    if (recording || processing || disabled) return;

    chunksRef.current = [];

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      if (onError) onError('Microphone permission denied. Please allow microphone access and try again.');
      return;
    }

    const mimeType = getSupportedMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop());
      clearTimeout(autoStopRef.current);

      const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
      chunksRef.current = [];

      if (blob.size < 1000) {
        if (onError) onError('Recording too short. Please hold the button and speak clearly.');
        setRecording(false);
        setProcessing(false);
        return;
      }

      setProcessing(true);
      try {
        const formData = new FormData();
        formData.append('audio', blob, `recording.${mimeType?.split('/')[1]?.split(';')[0] || 'webm'}`);
        formData.append('language', language);
        // role is intentionally omitted — derived from the JWT token server-side.
        // Sending role from the client would allow patients to request admin-scoped intent classification.

        // Content-Type must not be set manually — axios auto-sets multipart boundary
        const response = await api.post('/ai/voice-intent', formData, {
          headers: { 'Content-Type': undefined }
        });

        if (response.data?.success && onResult) {
          onResult(response.data.data);
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Voice request failed. Please try again.';
        if (onError) onError(msg);
      } finally {
        setProcessing(false);
      }

      setRecording(false);
    };

    recorder.start();
    mediaRecorderRef.current = recorder;
    setRecording(true);

    // Auto-stop after max duration — prevents runaway recording
    autoStopRef.current = setTimeout(() => stopRecording(), MAX_RECORDING_MS);
  }, [recording, processing, disabled, language, onResult, onError, stopRecording]);

  const handleClick = useCallback(() => {
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [recording, startRecording, stopRecording]);

  // ── Unsupported browser ────────────────────────────────────────────────────
  if (browserWarning && !supportsVoice()) {
    return (
      <div style={styles.warningBanner}>
        <span style={styles.warningIcon}>🎤</span>
        <span style={styles.warningText}>{browserWarning}</span>
      </div>
    );
  }

  // ── Mic button ─────────────────────────────────────────────────────────────
  const label      = processing ? 'Processing…' : recording ? 'Stop' : 'Speak';
  const ariaLabel  = processing ? 'Processing voice' : recording ? 'Stop recording' : 'Start voice recording';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || processing}
      aria-label={ariaLabel}
      style={{
        ...styles.micButton,
        ...(recording ? styles.micButtonRecording : {}),
        ...(disabled || processing ? styles.micButtonDisabled : {})
      }}
    >
      <span style={styles.micIcon}>{processing ? '⏳' : recording ? '⏹' : '🎤'}</span>
      <span style={styles.micLabel}>{label}</span>
    </button>
  );
}

const styles = {
  micButton: {
    display:        'inline-flex',
    alignItems:     'center',
    gap:            '6px',
    padding:        '10px 20px',
    minWidth:       '100px',
    minHeight:      '44px',      // CLAUDE.md MOBILE: touch target minimum 44×44px
    border:         '2px solid #6b7280',
    borderRadius:   '8px',
    background:     '#f9fafb',
    cursor:         'pointer',
    fontSize:       '16px',      // CLAUDE.md MOBILE: minimum 16px prevents iOS auto-zoom
    fontWeight:     '500',
    color:          '#374151',
    transition:     'all 0.2s ease'
  },
  micButtonRecording: {
    background:   '#fee2e2',
    borderColor:  '#ef4444',
    color:        '#b91c1c'
  },
  micButtonDisabled: {
    opacity:  0.5,
    cursor:   'not-allowed'
  },
  micIcon: {
    fontSize: '18px'
  },
  micLabel: {
    fontSize: '14px'
  },
  warningBanner: {
    display:      'flex',
    alignItems:   'center',
    gap:          '8px',
    padding:      '10px 14px',
    background:   '#fffbeb',
    border:       '1px solid #fcd34d',
    borderRadius: '8px',
    fontSize:     '14px',
    color:        '#92400e'
  },
  warningIcon: {
    fontSize: '18px',
    flexShrink: 0
  },
  warningText: {
    lineHeight: '1.4'
  }
};
